import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  Search,
  Download,
  Calendar,
  Users,
  TrendingUp,
  ShoppingBag,
  Phone,
  MapPin,
  X,
  ChevronDown,
  ChevronUp,
  FileSpreadsheet,
} from "lucide-react";
import * as XLSX from "xlsx";
import {
  get_customer_wise_report,
  server_post_data,
} from "../ServiceConnection/serviceconnection";
import { toast } from "react-toastify";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
const CustomerWiseReport = () => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30))
      .toISOString()
      .split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
    searchTerm: "",
    orderType: "All",
    paymentType: "All",
  });
  const [expandedCustomer, setExpandedCustomer] = useState(null);

  // Fetch report data
  const fetchReportData = useCallback(async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("startDate", filters.startDate);
      formData.append("endDate", filters.endDate);
      if (filters.orderType !== "All")
        formData.append("orderType", filters.orderType);
      if (filters.paymentType !== "All")
        formData.append("paymentType", filters.paymentType);

      const response = await server_post_data(
        get_customer_wise_report,
        formData,
      );

      // console.log("Resp", response.data.message);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      } else {
        const data = response.data.message || [];

        setReportData(data);
      }
    } catch (error) {
      console.error("Error fetching report:", error);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  }, [
    filters.startDate,
    filters.endDate,
    filters.orderType,
    filters.paymentType,
  ]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  // Group data by customer
  const customerData = useMemo(() => {
    const grouped = {};

    reportData.forEach((order) => {
      const customerKey = order.Phone || "Walk-in Customer";

      if (!grouped[customerKey]) {
        grouped[customerKey] = {
          name: order.Name || "Walk-in Customer",
          phone: order.Phone || "-",
          address: order.Address || "-",
          gst: order.GST || "-",
          orders: [],
          totalOrders: 0,
          totalAmount: 0,
          totalDiscount: 0,
          totalTax: 0,
          totalItems: 0,
        };
      }

      grouped[customerKey].orders.push(order);
      grouped[customerKey].totalOrders += 1;
      grouped[customerKey].totalAmount += parseFloat(order["Final Total"] || 0);
      grouped[customerKey].totalDiscount += parseFloat(order.Discount || 0);
      grouped[customerKey].totalTax += parseFloat(order.Tax || 0);
      grouped[customerKey].totalItems += parseInt(order["Qty."] || 0);
    });

    return Object.entries(grouped)
      .map(([phone, data]) => ({ ...data, phone }))
      .sort((a, b) => b.totalAmount - a.totalAmount);
  }, [reportData]);

  // Filter customers based on search
  const filteredCustomers = useMemo(() => {
    if (!filters.searchTerm) return customerData;

    const searchLower = filters.searchTerm.toLowerCase();
    return customerData.filter(
      (customer) =>
        customer.name.toLowerCase().includes(searchLower) ||
        customer.phone.toLowerCase().includes(searchLower) ||
        customer.address.toLowerCase().includes(searchLower),
    );
  }, [customerData, filters.searchTerm]);

  // Calculate summary statistics
  const summary = useMemo(() => {
    return {
      totalCustomers: filteredCustomers.length,
      totalOrders: filteredCustomers.reduce((sum, c) => sum + c.totalOrders, 0),
      totalRevenue: filteredCustomers.reduce(
        (sum, c) => sum + c.totalAmount,
        0,
      ),
      avgOrderValue:
        filteredCustomers.length > 0
          ? filteredCustomers.reduce((sum, c) => sum + c.totalAmount, 0) /
            filteredCustomers.reduce((sum, c) => sum + c.totalOrders, 0)
          : 0,
    };
  }, [filteredCustomers]);

  // Export to Excel
  const handleExport = useCallback(() => {
    const wb = XLSX.utils.book_new();

    // Sheet 1: Customer Summary
    const summaryData = filteredCustomers.map((customer) => ({
      "Customer Name": customer.name,
      Phone: customer.phone,
      Address: customer.address,
      "GST No.": customer.gst,
      "Total Orders": customer.totalOrders,
      "Total Items": customer.totalItems,
      "Sub Total": customer.orders
        .reduce((sum, o) => sum + parseFloat(o["Sub Total"] || 0), 0)
        .toFixed(2),
      "Total Discount": customer.totalDiscount.toFixed(2),
      SGST: customer.orders
        .reduce((sum, o) => sum + parseFloat(o["SGST Amount"] || 0), 0)
        .toFixed(2),
      CGST: customer.orders
        .reduce((sum, o) => sum + parseFloat(o["CGST Amount"] || 0), 0)
        .toFixed(2),
      "Total Tax": customer.totalTax.toFixed(2),
      "Final Total": customer.totalAmount.toFixed(2),
      "Avg Order Value": (customer.totalAmount / customer.totalOrders).toFixed(
        2,
      ),
    }));

    // Add grand total row
    summaryData.push({
      "Customer Name": "GRAND TOTAL",
      Phone: "",
      Address: "",
      "GST No.": "",
      "Total Orders": summary.totalOrders,
      "Total Items": filteredCustomers.reduce(
        (sum, c) => sum + c.totalItems,
        0,
      ),
      "Sub Total": filteredCustomers
        .reduce(
          (sum, c) =>
            sum +
            c.orders.reduce((s, o) => s + parseFloat(o["Sub Total"] || 0), 0),
          0,
        )
        .toFixed(2),
      "Total Discount": filteredCustomers
        .reduce((sum, c) => sum + c.totalDiscount, 0)
        .toFixed(2),
      SGST: filteredCustomers
        .reduce(
          (sum, c) =>
            sum +
            c.orders.reduce((s, o) => s + parseFloat(o["SGST Amount"] || 0), 0),
          0,
        )
        .toFixed(2),
      CGST: filteredCustomers
        .reduce(
          (sum, c) =>
            sum +
            c.orders.reduce((s, o) => s + parseFloat(o["CGST Amount"] || 0), 0),
          0,
        )
        .toFixed(2),
      "Total Tax": filteredCustomers
        .reduce((sum, c) => sum + c.totalTax, 0)
        .toFixed(2),
      "Final Total": summary.totalRevenue.toFixed(2),
      "Avg Order Value": summary.avgOrderValue.toFixed(2),
    });

    const ws1 = XLSX.utils.json_to_sheet(summaryData);
    ws1["!cols"] = [
      { wch: 25 },
      { wch: 15 },
      { wch: 25 },
      { wch: 18 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 14 },
      { wch: 10 },
      { wch: 10 },
      { wch: 12 },
      { wch: 14 },
      { wch: 16 },
    ];
    XLSX.utils.book_append_sheet(wb, ws1, "Customer Summary");

    // Sheet 2: Detailed Transactions
    const detailData = [];
    filteredCustomers.forEach((customer) => {
      customer.orders.forEach((order) => {
        detailData.push({
          "Invoice No.": order["Invoice No."],
          Date: order.Date,
          Time: order.Timestamp?.split(" ")[1] || "",
          "Customer Name": customer.name,
          Phone: customer.phone,
          Address: customer.address,
          "Item Name": order["Item Name"],
          Category: order.Category,
          Group: order["Group Name"],
          Variation: order.Variation,
          HSN: order.HSN,
          Qty: order["Qty."],
          Price: order.Price,
          "Sub Total": order["Sub Total"],
          Discount: order.Discount,
          "SGST %": order["SGST Rate"],
          "SGST Amt": order["SGST Amount"],
          "CGST %": order["CGST Rate"],
          "CGST Amt": order["CGST Amount"],
          "Total Tax": order.Tax,
          "Final Total": order["Final Total"],
          "Payment Type": order["Payment Type"],
          "Order Type": order["Order Type"],
          "Table No.": order["Table No."] || "-",
          Area: order.Area || "-",
          Server: order["Server Name"],
          Covers: order.Covers,
          Status: order.Status,
        });
      });
    });

    const ws2 = XLSX.utils.json_to_sheet(detailData);
    ws2["!cols"] = [
      { wch: 12 },
      { wch: 12 },
      { wch: 10 },
      { wch: 20 },
      { wch: 15 },
      { wch: 20 },
      { wch: 25 },
      { wch: 15 },
      { wch: 12 },
      { wch: 10 },
      { wch: 12 },
      { wch: 8 },
      { wch: 10 },
      { wch: 12 },
      { wch: 10 },
      { wch: 9 },
      { wch: 10 },
      { wch: 9 },
      { wch: 10 },
      { wch: 10 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 10 },
      { wch: 15 },
      { wch: 15 },
      { wch: 8 },
      { wch: 10 },
    ];
    XLSX.utils.book_append_sheet(wb, ws2, "Transaction Details");

    // Sheet 3: Payment Type Analysis
    const paymentAnalysis = {};
    filteredCustomers.forEach((customer) => {
      customer.orders.forEach((order) => {
        const pt = order["Payment Type"];
        if (!paymentAnalysis[pt]) {
          paymentAnalysis[pt] = { count: 0, amount: 0 };
        }
        paymentAnalysis[pt].count += 1;
        paymentAnalysis[pt].amount += parseFloat(order["Final Total"] || 0);
      });
    });

    const paymentData = Object.entries(paymentAnalysis).map(([type, data]) => ({
      "Payment Type": type,
      "No. of Orders": data.count,
      "Total Amount": data.amount.toFixed(2),
      Percentage: ((data.amount / summary.totalRevenue) * 100).toFixed(2) + "%",
    }));

    const ws3 = XLSX.utils.json_to_sheet(paymentData);
    ws3["!cols"] = [{ wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, ws3, "Payment Analysis");

    // Sheet 4: Top Items by Customer
    const itemAnalysis = [];
    filteredCustomers.forEach((customer) => {
      const items = {};
      customer.orders.forEach((order) => {
        const itemName = order["Item Name"];
        if (!items[itemName]) {
          items[itemName] = { qty: 0, amount: 0, category: order.Category };
        }
        items[itemName].qty += parseInt(order["Qty."] || 0);
        items[itemName].amount += parseFloat(order["Final Total"] || 0);
      });

      Object.entries(items).forEach(([item, data]) => {
        itemAnalysis.push({
          "Customer Name": customer.name,
          Phone: customer.phone,
          "Item Name": item,
          Category: data.category,
          "Total Qty": data.qty,
          "Total Amount": data.amount.toFixed(2),
        });
      });
    });

    const ws4 = XLSX.utils.json_to_sheet(itemAnalysis);
    ws4["!cols"] = [
      { wch: 20 },
      { wch: 15 },
      { wch: 25 },
      { wch: 15 },
      { wch: 12 },
      { wch: 15 },
    ];
    XLSX.utils.book_append_sheet(wb, ws4, "Item Analysis");

    // Generate filename with timestamp
    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .slice(0, -5);
    XLSX.writeFile(
      wb,
      `Customer_Wise_Report_${filters.startDate}_to_${filters.endDate}_${timestamp}.xlsx`,
    );
  }, [filteredCustomers, filters.startDate, filters.endDate, summary]);

  return (
    <MainLayout>
      <Header
        title="Customer-Wise Report"
        subtitle="Analyze customer transactions and purchase patterns"
      />
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        startDate: e.target.value,
                      }))
                    }
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#017268] focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        endDate: e.target.value,
                      }))
                    }
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#017268] focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Order Type
                </label>
                <select
                  value={filters.orderType}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      orderType: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#017268] focus:border-transparent"
                >
                  <option>All</option>
                  <option>Dine-In</option>
                  <option>Takeaway</option>
                  <option>Delivery</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Type
                </label>
                <select
                  value={filters.paymentType}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      paymentType: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#017268] focus:border-transparent"
                >
                  <option>All</option>
                  <option>Cash</option>
                  <option>Card</option>
                  <option>UPI</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search Customer
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Name, Phone, Address..."
                    value={filters.searchTerm}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        searchTerm: e.target.value,
                      }))
                    }
                    className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#017268] focus:border-transparent"
                  />
                  {filters.searchTerm && (
                    <button
                      onClick={() =>
                        setFilters((prev) => ({ ...prev, searchTerm: "" }))
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Customers</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {summary.totalCustomers}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Orders</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {summary.totalOrders}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center">
                  <ShoppingBag className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Revenue</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    ₹{summary.totalRevenue.toFixed(2)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Avg. Order Value</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    ₹{summary.avgOrderValue.toFixed(2)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-[#017268]/10 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-[#017268]" />
                </div>
              </div>
            </div>
          </div>

          {/* Customer List */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Customer Details
                </h2>
                <p className="text-sm text-gray-600">
                  Click on a customer to view detailed transactions
                </p>
              </div>

              {/* Quick Download Button */}
              {!loading && filteredCustomers.length > 0 && (
                <button
                  onClick={handleExport}
                  className="flex items-center gap-2 px-4 py-2 bg-[#017268] text-white rounded-lg hover:bg-[#015a52] transition-colors text-sm font-medium"
                >
                  <Download className="w-4 h-4" />
                  Download Report
                </button>
              )}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#017268]"></div>
                <span className="ml-3 text-gray-600">
                  Loading report data...
                </span>
              </div>
            ) : filteredCustomers.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>No customer data found for the selected filters</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredCustomers.map((customer) => (
                  <div
                    key={customer.phone}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    {/* Customer Summary Row */}
                    <div
                      onClick={() =>
                        setExpandedCustomer(
                          expandedCustomer === customer.phone
                            ? null
                            : customer.phone,
                        )
                      }
                      className="p-4 cursor-pointer"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-4">
                          <div>
                            <p className="font-semibold text-gray-900">
                              {customer.name}
                            </p>
                            <div className="flex items-center gap-1 text-sm text-gray-600 mt-1">
                              <Phone className="w-3 h-3" />
                              {customer.phone}
                            </div>
                            <div className="flex items-center gap-1 text-sm text-gray-600 mt-1">
                              <MapPin className="w-3 h-3" />
                              {customer.address}
                            </div>
                          </div>

                          <div className="text-center">
                            <p className="text-sm text-gray-600">Orders</p>
                            <p className="text-lg font-semibold text-gray-900">
                              {customer.totalOrders}
                            </p>
                          </div>

                          <div className="text-center">
                            <p className="text-sm text-gray-600">Items</p>
                            <p className="text-lg font-semibold text-gray-900">
                              {customer.totalItems}
                            </p>
                          </div>

                          <div className="text-center">
                            <p className="text-sm text-gray-600">Avg. Order</p>
                            <p className="text-lg font-semibold text-gray-900">
                              ₹
                              {(
                                customer.totalAmount / customer.totalOrders
                              ).toFixed(2)}
                            </p>
                          </div>

                          <div className="text-right">
                            <p className="text-sm text-gray-600">
                              Total Amount
                            </p>
                            <p className="text-xl font-bold text-[#017268]">
                              ₹{customer.totalAmount.toFixed(2)}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Tax: ₹{customer.totalTax.toFixed(2)} | Disc: ₹
                              {customer.totalDiscount.toFixed(2)}
                            </p>
                          </div>
                        </div>

                        <div className="ml-4">
                          {expandedCustomer === customer.phone ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Order Details */}
                    {expandedCustomer === customer.phone && (
                      <div className="px-4 pb-4 bg-gray-50">
                        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="text-left p-3 font-semibold text-gray-700">
                                    Invoice
                                  </th>
                                  <th className="text-left p-3 font-semibold text-gray-700">
                                    Date & Time
                                  </th>
                                  <th className="text-left p-3 font-semibold text-gray-700">
                                    Item
                                  </th>
                                  <th className="text-left p-3 font-semibold text-gray-700">
                                    Category
                                  </th>
                                  <th className="text-center p-3 font-semibold text-gray-700">
                                    Qty
                                  </th>
                                  <th className="text-right p-3 font-semibold text-gray-700">
                                    Price
                                  </th>
                                  <th className="text-right p-3 font-semibold text-gray-700">
                                    Discount
                                  </th>
                                  <th className="text-right p-3 font-semibold text-gray-700">
                                    Tax
                                  </th>
                                  <th className="text-right p-3 font-semibold text-gray-700">
                                    Total
                                  </th>
                                  <th className="text-center p-3 font-semibold text-gray-700">
                                    Payment
                                  </th>
                                  <th className="text-center p-3 font-semibold text-gray-700">
                                    Type
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {customer.orders.map((order, idx) => (
                                  <tr key={idx} className="hover:bg-gray-50">
                                    <td className="p-3 text-gray-900 font-medium">
                                      #{order["Invoice No."]}
                                    </td>
                                    <td className="p-3 text-gray-600">
                                      <div>{order.Date}</div>
                                      <div className="text-xs text-gray-500">
                                        {order.Timestamp?.split(" ")[1]}
                                      </div>
                                    </td>
                                    <td className="p-3 text-gray-900">
                                      {order["Item Name"]}
                                    </td>
                                    <td className="p-3 text-gray-600">
                                      {order.Category}
                                    </td>
                                    <td className="p-3 text-center text-gray-900">
                                      {order["Qty."]}
                                    </td>
                                    <td className="p-3 text-right text-gray-900">
                                      ₹{order.Price}
                                    </td>
                                    <td className="p-3 text-right text-red-600">
                                      -₹{order.Discount}
                                    </td>
                                    <td className="p-3 text-right text-gray-600">
                                      ₹{order.Tax}
                                    </td>
                                    <td className="p-3 text-right font-semibold text-gray-900">
                                      ₹{order["Final Total"]}
                                    </td>
                                    <td className="p-3 text-center">
                                      <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">
                                        {order["Payment Type"]}
                                      </span>
                                    </td>
                                    <td className="p-3 text-center">
                                      <span className="px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-700">
                                        {order["Order Type"]}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default CustomerWiseReport;
